import React, { useState, useEffect } from 'react';
import chestImage from '../assets/chest.png';
import { isAndroidNative, showRewardedAd, claimAfterAd } from '../utils/admobManager';
import { ADS_ENABLED } from '../config/admob';
import AndroidPromoModal from './AndroidPromoModal';
import { auth } from '../firebase';

const DURATIONS = [
  { label: '5 Minutes', ms: 5 * 60 * 1000, staminaCost: 5, multiplier: 0.02 },
  { label: '15 Minutes', ms: 15 * 60 * 1000, staminaCost: 10, multiplier: 0.05 },
  { label: '1 Hour', ms: 1 * 60 * 60 * 1000, staminaCost: 20, multiplier: 0.15 },
  { label: '8 Hours', ms: 8 * 60 * 60 * 1000, staminaCost: 45, multiplier: 0.50 },
  { label: '24 Hours', ms: 24 * 60 * 60 * 1000, staminaCost: 100, multiplier: 1.0 },
];

const DungeonTimer = ({
  session,
  playerData,
  totalMP,
  totalBoost,
  speedBoost = 0,
  xpBoost = 0,
  tokenBoost = 0,
  hasUnclaimedChest = false,
  claimDailyChestCallable,
  fetchTokenBalances,
  startDungeonRunCallable,
  claimDungeonRunCallable,
  previewDungeonRunCallable,
  speedUpDungeonRunCallable,
  restAtCampfireCallable,
  blessNextDungeonRunCallable,
  onOpenWheel
}) => {
  const calculateInitialTimeLeft = () => {
    if (playerData?.current_run && playerData.current_run.endTime > Date.now()) {
      return playerData.current_run.endTime - Date.now();
    }
    return 0;
  };

  const [selectedDuration, setSelectedDuration] = useState(DURATIONS[0]);
  const [timeLeft, setTimeLeft] = useState(calculateInitialTimeLeft());
  const [isLoading, setIsLoading] = useState(false);
  const [feedback, setFeedback] = useState('');
  const [isChestOpening, setIsChestOpening] = useState(false);
  const [chestRewardModal, setChestRewardModal] = useState(null);
  const [chestResultData, setChestResultData] = useState(null);
  const [promoModalPerk, setPromoModalPerk] = useState(null);

  const isAndroid = isAndroidNative();
  const currentRun = playerData?.current_run;
  const currentStamina = playerData?.stamina || 0;
  const maxStamina = playerData?.maxStamina || 10;
  const isStaminaDepleted = currentStamina <= 0;

  // Daily Android Perks calculations
  const today = new Date().toISOString().slice(0, 10);
  const perks = (playerData?.androidPerks?.date === today)
    ? (playerData?.androidPerks || {})
    : {};
  const speedupsRemaining = Math.max(0, 3 - (perks.speedupsUsed || 0));
  const restsRemaining = Math.max(0, 3 - (perks.restsUsed || 0));
  const doublesRemaining = Math.max(0, 5 - (perks.doublesUsed || 0));
  const blessingsRemaining = Math.max(0, 3 - (perks.blessingsUsed || 0));
  const activeBlessing = perks.activeBlessing || null;

  // Instant client-side estimate: shown immediately and as a fallback
  const speedFactor = Math.pow(2, speedBoost / 100);
  let activeDurationMs = Math.floor(selectedDuration.ms / speedFactor);
  let bonusMultiplier = selectedDuration.multiplier;

  activeDurationMs = activeDurationMs * (isStaminaDepleted ? 2 : 1);
  const activeStaminaCost = selectedDuration.staminaCost * (isStaminaDepleted ? 2 : 1);
  const finalMultiplier = bonusMultiplier * (isStaminaDepleted ? 0.5 : 1);

  const expectedPoints = Math.floor(totalMP * (1 + totalBoost / 100) * (1 + tokenBoost / 100) * finalMultiplier);

  // Server-verified preview
  const [livePreview, setLivePreview] = useState(null);
  const durationIndex = DURATIONS.indexOf(selectedDuration);

  useEffect(() => {
    if (!session || !previewDungeonRunCallable || currentRun) return;
    if (!auth.currentUser || auth.currentUser.uid !== session.actor.toString()) {
      setLivePreview(null);
      return;
    }
    let cancelled = false;

    const fetchPreview = async () => {
      try {
        const result = await previewDungeonRunCallable({ durationIndex });
        if (!cancelled && result.data && result.data.success) {
          setLivePreview(result.data);
        }
      } catch (err) {
        console.error('Failed to fetch live run preview:', err);
      }
    };

    fetchPreview();
    const interval = setInterval(fetchPreview, 20000);
    return () => {
      cancelled = true;
      clearInterval(interval);
    };
  }, [durationIndex, session, previewDungeonRunCallable, currentRun]);

  const displayExpectedPoints = livePreview ? livePreview.expectedPoints : expectedPoints;
  const displayDurationMs = livePreview ? livePreview.activeDurationMs : activeDurationMs;
  const displayStaminaCost = livePreview ? livePreview.activeStaminaCost : activeStaminaCost;

  useEffect(() => {
    let interval = null;
    if (currentRun && currentRun.endTime > Date.now()) {
      setTimeLeft(currentRun.endTime - Date.now());
      interval = setInterval(() => {
        const remaining = currentRun.endTime - Date.now();
        if (remaining <= 0) {
          setTimeLeft(0);
          clearInterval(interval);
        } else {
          setTimeLeft(remaining);
        }
      }, 1000);
    } else {
      setTimeLeft(0);
    }
    return () => {
      if (interval) clearInterval(interval);
    };
  }, [currentRun]);

  const formatTime = (ms) => {
    if (ms <= 0) return "00:00:00";
    const totalSeconds = Math.floor(ms / 1000);
    const hours = Math.floor(totalSeconds / 3600);
    const minutes = Math.floor((totalSeconds % 3600) / 60);
    const seconds = totalSeconds % 60;
    return `${hours.toString().padStart(2, '0')}:${minutes.toString().padStart(2, '0')}:${seconds.toString().padStart(2, '0')}`;
  };

  const handleStartRun = async () => {
    if (!session || !startDungeonRunCallable) return;
    if (!auth.currentUser || auth.currentUser.uid !== session.actor.toString()) {
      setFeedback('⚠️ Session not verified. Please click "VERIFY IDENTITY" at the top.');
      return;
    }
    setIsLoading(true);
    setFeedback('');
    try {
      const result = await startDungeonRunCallable({
        durationIndex: DURATIONS.indexOf(selectedDuration),
        clientMP: totalMP,
        clientTotalBoost: totalBoost,
        clientSpeedBoost: speedBoost,
        clientTokenBoost: tokenBoost
      });
      if (result.data && result.data.success) {
        setFeedback('⚔️ Dungeon run started!');
      } else {
        setFeedback(`Failed to start: ${result.data?.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error(err);
      setFeedback(`Error starting run: ${err.message}`);
    }
    setIsLoading(false);
  };

  // 1. Standard Claim
  const handleClaimRun = async () => {
    if (!session || !currentRun || !claimDungeonRunCallable) return;
    if (!auth.currentUser || auth.currentUser.uid !== session.actor.toString()) {
      setFeedback('⚠️ Session not verified. Please click "VERIFY IDENTITY" at the top.');
      return;
    }

    if (currentRun.endTime > Date.now()) {
      setFeedback('Run is not finished yet!');
      return;
    }

    setIsLoading(true);
    setFeedback('');
    try {
      const result = await claimDungeonRunCallable({ xpBoost, doubleWithAd: false });
      if (result.data && result.data.success) {
        setFeedback(`🎉 Claimed successfully! Earned ${result.data.rewardPoints} points and ${result.data.xpReward} XP.`);
      } else {
        setFeedback(`Failed to claim: ${result.data?.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error(err);
      const msg = err?.message || '';
      if (msg.includes("No active run to claim") || msg.includes("already claimed")) {
        setFeedback('✅ Run was already claimed on the server! Refreshing...');
        setTimeout(() => {
          window.location.reload();
        }, 1200);
      } else {
        setFeedback(`Error claiming run: ${msg}`);
      }
    }
    setIsLoading(false);
  };

  // 2. 👑 Goblin's Greed (2x Double Claim with Rewarded Ad)
  const handleClaimRunDoubled = async () => {
    if (!session || !currentRun || !claimDungeonRunCallable) return;

    if (!isAndroid) {
      setPromoModalPerk("Goblin's Greed (2x Doubler)");
      return;
    }

    if (doublesRemaining <= 0) {
      setFeedback('Daily 2x Doubler limit reached (5/5). Resets tomorrow!');
      return;
    }

    setIsLoading(true);
    setFeedback('Loading video ad...');
    try {
      await showRewardedAd('dungeonDoubler');
      setFeedback('Ad completed! Claiming double rewards...');

      const result = await claimAfterAd(claimDungeonRunCallable, { xpBoost, doubleWithAd: true });
      if (result.data && result.data.success) {
        setFeedback(`👑 Goblin's Greed Activated! Claimed DOUBLE: ${result.data.rewardPoints} Daily Points & ${result.data.xpReward} XP!`);
      } else {
        setFeedback(`Failed to claim: ${result.data?.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error(err);
      const msg = err?.message || '';
      if (msg.includes("No active run to claim") || msg.includes("already claimed")) {
        setFeedback('✅ Run was already claimed on the server! Refreshing...');
        setTimeout(() => {
          window.location.reload();
        }, 1200);
      } else {
        setFeedback(`Error: ${msg}`);
      }
    }
    setIsLoading(false);
  };

  // 3. ⚡ Torch of Haste (Skip 30 minutes)
  const handleSpeedUpRun = async () => {
    if (!currentRun || timeLeft <= 0) return;

    if (!isAndroid) {
      setPromoModalPerk('Torch of Haste (Time Fast-Forward)');
      return;
    }

    if (speedupsRemaining <= 0) {
      setFeedback('Daily Torch of Haste limit reached (3/3). Resets tomorrow!');
      return;
    }

    if (!speedUpDungeonRunCallable) return;
    setIsLoading(true);
    setFeedback('Loading video ad...');
    try {
      await showRewardedAd('dungeonSpeedup');
      setFeedback('Ad completed! Fast-forwarding expedition...');

      const result = await claimAfterAd(speedUpDungeonRunCallable);
      if (result.data && result.data.success) {
        setFeedback(`⚡ Torch of Haste Used! Shaved 30 minutes off your run (${result.data.speedupsRemaining} left today).`);
      } else {
        setFeedback(`Failed: ${result.data?.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error(err);
      setFeedback(`Error: ${err.message}`);
    }
    setIsLoading(false);
  };

  // 4. 🔥 Campfire Rest (+50 Stamina Recovery)
  const handleCampfireRest = async () => {
    if (!isAndroid) {
      setPromoModalPerk('Campfire Rest (Stamina Recovery)');
      return;
    }

    if (restsRemaining <= 0) {
      setFeedback('Daily Campfire Rest limit reached (3/3). Resets tomorrow!');
      return;
    }

    if (currentStamina >= maxStamina) {
      setFeedback('Your stamina is already at maximum capacity!');
      return;
    }

    if (!restAtCampfireCallable) return;
    setIsLoading(true);
    setFeedback('Loading video ad...');
    try {
      await showRewardedAd('campfireRest');
      setFeedback('Ad completed! Resting at the campfire...');

      const result = await claimAfterAd(restAtCampfireCallable);
      if (result.data && result.data.success) {
        setFeedback(`🔥 Campfire Rest complete! Restored +${result.data.staminaGained} Stamina (${result.data.restsRemaining} left today).`);
      } else {
        setFeedback(`Failed: ${result.data?.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error(err);
      setFeedback(`Error: ${err.message}`);
    }
    setIsLoading(false);
  };

  // 5. 🔮 Shrine of the Dragon God Blessing
  const handleApplyBlessing = async (blessingType) => {
    if (!isAndroid) {
      setPromoModalPerk('Shrine of the Dragon God');
      return;
    }

    if (blessingsRemaining <= 0) {
      setFeedback('Daily Shrine Blessing limit reached (3/3). Resets tomorrow!');
      return;
    }

    if (!blessNextDungeonRunCallable) return;
    setIsLoading(true);
    setFeedback('Loading video ad...');
    try {
      await showRewardedAd('dragonShrine');
      setFeedback('Ad completed! Praying at the ancient shrine...');

      const result = await claimAfterAd(blessNextDungeonRunCallable, { blessingType });
      if (result.data && result.data.success) {
        const b = result.data.activeBlessing;
        setFeedback(`🔮 Blessing granted! +${b.bonus}% ${b.type.toUpperCase()} applied to your next dungeon run!`);
      } else {
        setFeedback(`Failed: ${result.data?.message || 'Unknown error'}`);
      }
    } catch (err) {
      console.error(err);
      setFeedback(`Error: ${err.message}`);
    }
    setIsLoading(false);
  };

  const handleClaimChest = async () => {
    if (!claimDailyChestCallable || !session) return;
    setIsLoading(true);
    setFeedback('');
    setIsChestOpening(true);
    try {
      await new Promise(resolve => setTimeout(resolve, 2000));
      const result = await claimDailyChestCallable();
      if (result.data && result.data.success) {
        setChestResultData(result.data);
        setChestRewardModal(true);
        if (fetchTokenBalances) fetchTokenBalances();
      } else {
        setFeedback(`Failed to claim chest: ${result.data?.message || 'Unknown error'}`);
      }
    } catch (error) {
      console.error(error);
      setFeedback(`Error claiming chest: ${error.message}`);
    } finally {
      setIsChestOpening(false);
      setIsLoading(false);
    }
  };

  if (hasUnclaimedChest || chestRewardModal) {
    return (
      <div style={{ backgroundColor: 'rgba(0,0,0,0.8)', padding: '20px', borderRadius: '10px', color: '#fff', maxWidth: '600px', margin: '0 auto', textAlign: 'center', position: 'relative' }}>
        <h2 style={{ color: '#ff9800' }}>Daily Settlement Complete!</h2>
        <p>You have an unclaimed Daily Dungeon Chest from the leaderboard settlement.</p>
        <p>You must claim your chest before you can start new dungeon runs!</p>

        <div style={{ margin: '30px 0' }}>
          <div className={`chest-container ${isChestOpening ? 'shake-animation' : ''}`} style={{ cursor: isLoading ? 'default' : 'pointer' }} onClick={!isLoading ? handleClaimChest : undefined}>
            <img src={chestImage} alt="Daily Chest" style={{ width: '150px', height: '150px', objectFit: 'contain' }} />
          </div>
        </div>

        <button onClick={handleClaimChest} disabled={isLoading} style={{ padding: '15px 30px', fontSize: '1.2em', backgroundColor: '#9c27b0', color: 'white', border: 'none', borderRadius: '5px', cursor: 'pointer' }}>
          {isLoading ? 'Opening Chest...' : 'Claim Daily Chest'}
        </button>

        {chestRewardModal && chestResultData && (
          <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, backgroundColor: 'rgba(0,0,0,0.85)', display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 9999 }}>
            <div style={{ backgroundColor: '#1e1e1e', padding: '30px', borderRadius: '12px', width: '90%', maxWidth: '450px', border: '2px solid #ff9800', textAlign: 'center', boxShadow: '0 0 25px rgba(255,152,0,0.3)' }}>
              <h3 style={{ color: '#ff9800', marginTop: 0, fontSize: '1.8em' }}>🎁 Chest Unboxed!</h3>
              <div style={{ margin: '20px 0', padding: '15px', background: 'rgba(0,0,0,0.4)', borderRadius: '8px', border: '1px solid #444' }}>
                <p style={{ fontSize: '1.1em', color: '#fff', marginBottom: '10px' }}>
                  <strong>Leaderboard Points:</strong> +{chestResultData.rewards?.pointsRollover || 0}
                </p>

                {(!chestResultData.rewards?.nfts || chestResultData.rewards.nfts.length === 0) ? (
                  <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(255,75,43,0.15)', border: '1px solid #ff4b2b', borderRadius: '6px' }}>
                    <p style={{ color: '#ff4b2b', fontWeight: 'bold', margin: '0 0 5px 0' }}>Bummer, no NFT this time!</p>
                    <p style={{ color: '#ccc', fontSize: '0.9em', margin: 0 }}>Better luck next time, or raise your NFT chance with Gangs or Gear for better probabilities!</p>
                  </div>
                ) : (
                  <div style={{ marginTop: '15px', padding: '10px', background: 'rgba(76,175,80,0.15)', border: '1px solid #4caf50', borderRadius: '6px' }}>
                    <p style={{ color: '#4caf50', fontWeight: 'bold', margin: 0 }}>🎉 Awesome loot! You won {chestResultData.rewards.nfts.length} NFT(s)! Check your inventory.</p>
                  </div>
                )}
              </div>

              <button onClick={() => { setChestRewardModal(false); window.location.reload(); }} style={{ width: '100%', padding: '12px', backgroundColor: '#28a745', color: 'white', border: 'none', borderRadius: '6px', fontSize: '1.1em', fontWeight: 'bold', cursor: 'pointer' }}>
                Awesome, Claim & Continue
              </button>
            </div>
          </div>
        )}

        {feedback && (
          <div style={{ marginTop: '20px', padding: '10px', backgroundColor: '#333', borderRadius: '5px', color: '#4caf50' }}>
            {feedback}
          </div>
        )}

        <style>{`
        @keyframes shake {
          0% { transform: translate(1px, 1px) rotate(0deg); }
          10% { transform: translate(-1px, -2px) rotate(-1deg); }
          20% { transform: translate(-3px, 0px) rotate(1deg); }
          30% { transform: translate(3px, 2px) rotate(0deg); }
          40% { transform: translate(1px, -1px) rotate(1deg); }
          50% { transform: translate(-1px, 2px) rotate(-1deg); }
          60% { transform: translate(-3px, 1px) rotate(0deg); }
          70% { transform: translate(3px, 1px) rotate(-1deg); }
          80% { transform: translate(-1px, -1px) rotate(1deg); }
          90% { transform: translate(1px, 2px) rotate(0deg); }
          100% { transform: translate(1px, -2px) rotate(-1deg); }
        }
        .shake-animation {
          animation: shake 0.5s;
          animation-iteration-count: infinite;
        }
      `}</style>
      </div>
    );
  }

  return (
    <div style={{ backgroundColor: 'rgba(0,0,0,0.85)', padding: '20px', borderRadius: '12px', color: '#fff', maxWidth: '620px', margin: '0 auto', boxShadow: '0 8px 30px rgba(0,0,0,0.7)', border: '1px solid #333' }}>
      
      {/* 🎰 Wheel of Riches Banner */}
      {ADS_ENABLED && (
        <div style={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          background: 'linear-gradient(90deg, rgba(255, 215, 0, 0.15), rgba(0, 229, 255, 0.15))',
          border: '1px solid #ffd700',
          borderRadius: '10px',
          padding: '10px 16px',
          marginBottom: '18px',
          boxShadow: '0 0 15px rgba(255, 215, 0, 0.15)'
        }}>
          <div style={{ display: 'flex', alignItems: 'center', gap: '10px' }}>
            <span style={{ fontSize: '1.6em' }}>🎰</span>
            <div style={{ textAlign: 'left' }}>
              <strong style={{ color: '#ffd700', fontSize: '0.95em' }}>Wheel of Riches</strong>
              <div style={{ fontSize: '0.8em', color: '#ccc' }}>1 free spin a day, plus 3 more by watching a video</div>
            </div>
          </div>
          <button
            onClick={() => onOpenWheel ? onOpenWheel() : setPromoModalPerk('The Wheel of Riches')}
            style={{
              padding: '8px 16px',
              background: 'linear-gradient(135deg, #ffd700, #ff9800)',
              color: '#000',
              fontWeight: 'bold',
              fontSize: '0.85em',
              borderRadius: '6px',
              border: 'none',
              cursor: 'pointer',
              boxShadow: '0 0 10px rgba(255, 215, 0, 0.3)'
            }}
          >
            Spin Wheel ➔
          </button>
        </div>
      )}

      <h2 style={{ textAlign: 'center', color: '#ff9800', margin: '0 0 15px' }}>WHATIF Dungeon of Riches</h2>

      {/* Stats Overview */}
      <div style={{ display: 'flex', flexWrap: 'wrap', justifyContent: 'space-between', marginBottom: '20px', padding: '12px', backgroundColor: '#222', borderRadius: '8px', gap: '10px', border: '1px solid #333' }}>
        <div style={{ flex: '1 1 30%' }}>
          <span style={{ color: '#aaa', fontSize: '0.85em' }}>Mining Power:</span> <strong style={{ color: '#00e5ff' }}>{totalMP}</strong>
        </div>
        <div style={{ flex: '1 1 30%' }}>
          <span style={{ color: '#aaa', fontSize: '0.85em' }}>Total Boost:</span> <strong style={{ color: '#4caf50' }}>{totalBoost}%</strong>
        </div>
        <div style={{ flex: '1 1 30%' }}>
          <span style={{ color: '#aaa', fontSize: '0.85em' }}>Speed Boost:</span> <strong style={{ color: '#ffeb3b' }}>{speedBoost}%</strong>
        </div>
        <div style={{ flex: '1 1 30%' }}>
          <span style={{ color: '#aaa', fontSize: '0.85em' }}>Points Boost:</span> <strong style={{ color: '#ff9800' }}>{tokenBoost}%</strong>
        </div>
        <div style={{ flex: '1 1 30%' }}>
          <span style={{ color: '#aaa', fontSize: '0.85em' }}>XP Boost:</span> <strong style={{ color: '#e91e63' }}>{xpBoost}%</strong>
        </div>
        <div style={{ flex: '1 1 30%', display: 'flex', alignItems: 'center', gap: '6px', flexWrap: 'wrap' }}>
          <span style={{ color: '#aaa', fontSize: '0.85em' }}>Stamina:</span>
          <strong style={{ color: isStaminaDepleted ? '#f44336' : '#fff' }}>{currentStamina} / {maxStamina}</strong>
          {ADS_ENABLED && currentStamina < maxStamina && (
            <button
              onClick={handleCampfireRest}
              disabled={isLoading || restsRemaining <= 0}
              title="Rest by Campfire to recover +50 Stamina"
              style={{
                padding: '2px 8px',
                fontSize: '0.7em',
                fontWeight: 'bold',
                backgroundColor: restsRemaining > 0 ? '#ff5722' : '#555',
                color: '#fff',
                border: 'none',
                borderRadius: '4px',
                cursor: restsRemaining > 0 ? 'pointer' : 'not-allowed',
                boxShadow: restsRemaining > 0 ? '0 0 8px rgba(255, 87, 34, 0.4)' : 'none'
              }}
            >
              🎬 Watch Video: +50 Stamina {isAndroid ? `(${restsRemaining}/3)` : '(Android)'}
            </button>
          )}
        </div>
      </div>

      {currentRun ? (
        <div style={{ textAlign: 'center', padding: '24px', border: '2px solid #4caf50', borderRadius: '12px', background: 'rgba(76, 175, 80, 0.05)' }}>
          <h3 style={{ margin: '0 0 10px', color: '#4caf50' }}>⚔️ Run In Progress</h3>
          <div style={{ fontSize: '2.4em', fontFamily: 'monospace', margin: '15px 0', color: timeLeft > 0 ? '#ffeb3b' : '#4caf50', fontWeight: 'bold' }}>
            {formatTime(timeLeft)}
          </div>

          {timeLeft > 0 ? (
            <div>
              <p style={{ color: '#aaa', margin: '0 0 15px' }}>Hero is traversing the dungeon...</p>
              
              {/* ⚡ Torch of Haste Speed-up Button */}
              {ADS_ENABLED && (
                <button
                  onClick={handleSpeedUpRun}
                  disabled={isLoading || speedupsRemaining <= 0}
                  style={{
                    padding: '12px 22px',
                    fontSize: '1em',
                    fontWeight: 'bold',
                    background: speedupsRemaining > 0 ? 'linear-gradient(135deg, #ffeb3b, #ff9800)' : '#444',
                    color: speedupsRemaining > 0 ? '#000' : '#888',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: speedupsRemaining > 0 ? 'pointer' : 'not-allowed',
                    boxShadow: speedupsRemaining > 0 ? '0 0 18px rgba(255, 235, 59, 0.4)' : 'none',
                    transition: 'all 0.2s'
                  }}
                >
                  🎬 Torch of Haste: Watch Video, -30m {isAndroid ? `(${speedupsRemaining}/3 Today)` : '(📱 Android App)'}
                </button>
              )}
            </div>
          ) : (
            <div style={{ display: 'flex', flexDirection: 'column', gap: '12px', maxWidth: '420px', margin: '0 auto' }}>
              <div style={{ color: '#4caf50', fontWeight: 'bold', fontSize: '1.2em', marginBottom: '5px' }}>
                🎉 Expedition Complete!
              </div>

              {/* 👑 Goblin's Greed 2x Claim */}
              {ADS_ENABLED && (
                <button
                  onClick={handleClaimRunDoubled}
                  disabled={isLoading || doublesRemaining <= 0}
                  style={{
                    padding: '14px 20px',
                    fontSize: '1.1em',
                    fontWeight: 'bold',
                    background: doublesRemaining > 0 ? 'linear-gradient(135deg, #ffd700, #ff9800)' : '#555',
                    color: doublesRemaining > 0 ? '#000' : '#888',
                    border: 'none',
                    borderRadius: '8px',
                    cursor: doublesRemaining > 0 ? 'pointer' : 'not-allowed',
                    boxShadow: doublesRemaining > 0 ? '0 0 20px rgba(255, 215, 0, 0.4)' : 'none'
                  }}
                >
                  🎬 Goblin's Greed: Watch Video for 2x Daily Points & XP (+{(currentRun.expectedPoints || 0) * 2} Pts) {isAndroid ? `(${doublesRemaining}/5)` : '(Android)'}
                  <div style={{ fontSize: '0.7em', fontWeight: 'normal', marginTop: '4px' }}>The extra points count toward your daily score, not your Store balance.</div>
                </button>
              )}

              {/* Standard Claim */}
              <button
                onClick={handleClaimRun}
                disabled={isLoading}
                style={{
                  padding: '10px 16px',
                  fontSize: '0.95em',
                  backgroundColor: '#333',
                  color: '#bbb',
                  border: '1px solid #555',
                  borderRadius: '6px',
                  cursor: 'pointer'
                }}
              >
                Standard Claim (+{currentRun.expectedPoints || 0} Points)
              </button>
            </div>
          )}
        </div>
      ) : (
        <div>
          {/* 🔮 Shrine of the Dragon God */}
          {ADS_ENABLED && (
            <div style={{
              backgroundColor: 'rgba(25, 20, 45, 0.75)',
              border: '1px solid #9c27b0',
              borderRadius: '10px',
              padding: '14px',
              marginBottom: '20px',
              boxShadow: '0 0 15px rgba(156, 39, 176, 0.2)'
            }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '10px' }}>
                <strong style={{ color: '#e040fb', fontSize: '0.95em' }}>🔮 Shrine of the Dragon God</strong>
                <span style={{ fontSize: '0.8em', color: '#bbb' }}>{isAndroid ? `${blessingsRemaining}/3 Blessings Today` : 'Android Exclusive'}</span>
              </div>

              {activeBlessing ? (
                <div style={{
                  backgroundColor: 'rgba(224, 64, 251, 0.15)',
                  border: '1px solid #e040fb',
                  borderRadius: '6px',
                  padding: '10px',
                  fontSize: '0.9em',
                  color: '#e040fb',
                  fontWeight: 'bold',
                  textAlign: 'center'
                }}>
                  ✨ Active Blessing: +{activeBlessing.bonus}% {activeBlessing.type.toUpperCase()} will apply to this run!
                </div>
              ) : (
                <div>
                  <p style={{ margin: '0 0 10px', fontSize: '0.85em', color: '#ccc' }}>
                    Watch a video for a blessing on your next expedition:
                  </p>
                  <div style={{ display: 'flex', gap: '8px', flexWrap: 'wrap' }}>
                    <button
                      onClick={() => handleApplyBlessing('mp')}
                      disabled={isLoading || blessingsRemaining <= 0}
                      style={{
                        flex: '1 1 30%',
                        padding: '8px',
                        fontSize: '0.85em',
                        fontWeight: 'bold',
                        backgroundColor: '#3f51b5',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: blessingsRemaining > 0 ? 'pointer' : 'not-allowed'
                      }}
                    >
                      ⚔️ +25% MP
                    </button>
                    <button
                      onClick={() => handleApplyBlessing('speed')}
                      disabled={isLoading || blessingsRemaining <= 0}
                      style={{
                        flex: '1 1 30%',
                        padding: '8px',
                        fontSize: '0.85em',
                        fontWeight: 'bold',
                        backgroundColor: '#009688',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: blessingsRemaining > 0 ? 'pointer' : 'not-allowed'
                      }}
                    >
                      ⚡ +20% Speed
                    </button>
                    <button
                      onClick={() => handleApplyBlessing('xp')}
                      disabled={isLoading || blessingsRemaining <= 0}
                      style={{
                        flex: '1 1 30%',
                        padding: '8px',
                        fontSize: '0.85em',
                        fontWeight: 'bold',
                        backgroundColor: '#e91e63',
                        color: '#fff',
                        border: 'none',
                        borderRadius: '6px',
                        cursor: blessingsRemaining > 0 ? 'pointer' : 'not-allowed'
                      }}
                    >
                      🌟 +25% XP
                    </button>
                  </div>
                </div>
              )}
            </div>
          )}

          <h3 style={{ textAlign: 'center', margin: '0 0 12px' }}>Select Run Duration</h3>
          <div style={{ display: 'flex', justifyContent: 'space-around', margin: '0 0 20px', flexWrap: 'wrap', gap: '8px' }}>
            {DURATIONS.map((d, i) => (
              <button
                key={i}
                onClick={() => { setSelectedDuration(d); setLivePreview(null); }}
                style={{
                  padding: '10px',
                  backgroundColor: selectedDuration.label === d.label ? '#ff9800' : '#333',
                  color: '#fff',
                  border: selectedDuration.label === d.label ? '1px solid #ffd700' : '1px solid #444',
                  borderRadius: '6px',
                  cursor: 'pointer',
                  flex: '1 1 20%',
                  fontWeight: selectedDuration.label === d.label ? 'bold' : 'normal'
                }}
              >
                {d.label}
              </button>
            ))}
          </div>

          <div style={{ backgroundColor: '#222', padding: '15px', borderRadius: '8px', marginBottom: '20px', border: '1px solid #333' }}>
            <p style={{ margin: '5px 0' }}><strong>Est. Reward:</strong> <span style={{ color: '#00e5ff' }}>~{displayExpectedPoints} Points</span></p>
            <p style={{ margin: '5px 0' }}><strong>Actual Time:</strong> {formatTime(displayDurationMs)}</p>
            <p style={{ margin: '5px 0' }}><strong>Stamina Cost:</strong> {displayStaminaCost}</p>
          </div>

          {isStaminaDepleted && (
            <div style={{ padding: '12px', backgroundColor: 'rgba(244, 67, 54, 0.15)', border: '1px solid #f44336', borderRadius: '8px', marginBottom: '20px' }}>
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                <strong style={{ color: '#f44336' }}>Warning: Stamina Depleted!</strong>
                {ADS_ENABLED && (
                  <button
                    onClick={handleCampfireRest}
                    style={{
                      padding: '4px 10px',
                      fontSize: '0.8em',
                      fontWeight: 'bold',
                      backgroundColor: '#ff5722',
                      color: '#fff',
                      border: 'none',
                      borderRadius: '4px',
                      cursor: 'pointer'
                    }}
                  >
                    🎬 Watch Video: +50 Stamina
                  </button>
                )}
              </div>
              <p style={{ fontSize: '0.85em', margin: '6px 0 0', color: '#ddd' }}>
                Running a dungeon without stamina doubles duration, costs 2x stamina, and halves rewards.
              </p>
            </div>
          )}

          <button
            onClick={handleStartRun}
            disabled={isLoading || totalMP <= 0}
            style={{
              width: '100%',
              padding: '15px',
              fontSize: '1.2em',
              fontWeight: 'bold',
              backgroundColor: totalMP > 0 ? '#ff9800' : '#555',
              color: totalMP > 0 ? '#000' : '#888',
              border: 'none',
              borderRadius: '8px',
              cursor: totalMP > 0 ? 'pointer' : 'not-allowed',
              boxShadow: totalMP > 0 ? '0 0 15px rgba(255, 152, 0, 0.3)' : 'none'
            }}
          >
            {isLoading ? 'Starting...' : (totalMP > 0 ? 'Start Dungeon Run' : 'Stake MP to Run')}
          </button>
        </div>
      )}

      {feedback && (
        <div style={{ marginTop: '20px', padding: '12px', backgroundColor: '#222', border: '1px solid #444', borderRadius: '8px', textAlign: 'center', fontSize: '0.95em', color: '#ffeb3b' }}>
          {feedback}
        </div>
      )}

      {/* Android Promo Modal for Desktop/Web Players */}
      <AndroidPromoModal
        isOpen={!!promoModalPerk}
        onClose={() => setPromoModalPerk(null)}
        perkName={promoModalPerk || 'This perk'}
      />
    </div>
  );
};

export default DungeonTimer;